import React from 'react';
import { Calendar, MapPin, Users, Edit, Eye, ExternalLink } from 'lucide-react';
import CuratorToolsDropdown from './CuratorToolsDropdown';

/**
 * EventHeaderBanner Component
 * 
 * Displays prominent event information when an event is tied to a collection.
 * Shows event dates, venue, status, and quick stats.
 * 
 * Props:
 * - eventDetails: object (event data)
 * - artworkCount: number (total artworks in collection)
 * - artistCount: number (total artists)
 * - checkInStats: object { total, checkedIn }
 * - curatorToolsProps: object (props for CuratorToolsDropdown)
 * - artworks: array (full artworks array for value calculations)
 */
const EventHeaderBanner = ({
  eventDetails,
  artworkCount = 0,
  artistCount = 0,
  checkInStats = { total: 0, checkedIn: 0 },
  curatorToolsProps,
  artworks = []
}) => {
  if (!eventDetails) return null;

  // Determine event status
  const now = new Date();
  const eventStart = new Date(eventDetails.event_start);
  const eventEnd = new Date(eventDetails.event_end);
  
  let status = 'Upcoming';
  let statusColor = '#3b82f6'; // Blue
  
  if (now > eventEnd) {
    status = 'Past';
    statusColor = '#9ca3af'; // Gray
  } else if (now >= eventStart && now <= eventEnd) {
    status = 'Active Now';
    statusColor = '#10b981'; // Green
  }

  // Calculate check-in percentage
  const checkInPercentage = checkInStats.total > 0 
    ? Math.round((checkInStats.checkedIn / checkInStats.total) * 100)
    : 0;

  return (
    <div className="event-header-banner">
      <div className="event-banner-container">
        {/* Left: Event Image & Info */}
        <div className="event-banner-left">
          {eventDetails.cover_image && (
            <div className="event-banner-image">
              <img 
                src={eventDetails.cover_image} 
                alt={eventDetails.name}
                className="event-banner-img"
              />
              <div className="event-banner-overlay">
                <span className="event-status-badge" style={{ backgroundColor: statusColor }}>
                  {status}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Middle: Event Details */}
        <div className="event-banner-details">
          <h2 className="event-banner-title">{eventDetails.name}</h2>

          {/* Dates */}
          {eventDetails.event_start && (
            <div className="event-banner-meta-item">
              <Calendar size={18} className="event-banner-icon" />
              <div className="event-banner-meta-content">
                <span className="event-banner-meta-label">Exhibition Dates</span>
                <span className="event-banner-meta-value">
                  {new Date(eventDetails.event_start).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  {eventDetails.event_end && (
                    <>
                      {' – '}
                      {new Date(eventDetails.event_end).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Venue */}
          {eventDetails.venue_name && (
            <div className="event-banner-meta-item">
              <MapPin size={18} className="event-banner-icon" />
              <div className="event-banner-meta-content">
                <span className="event-banner-meta-label">Venue</span>
                <span className="event-banner-meta-value">
                  {eventDetails.venue_name}
                  {eventDetails.venue_address && `, ${eventDetails.venue_address.split(',')[0]}`}
                </span>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="event-banner-stats">
            {artworkCount > 0 && (
              <div className="stat-item">
                <span className="stat-label">Artworks</span>
                <span className="stat-value">{artworkCount}</span>
              </div>
            )}
            {artistCount > 0 && (
              <div className="stat-item">
                <span className="stat-label">Artists</span>
                <span className="stat-value">{artistCount}</span>
              </div>
            )}
            {checkInStats.total > 0 && (
              <div className="stat-item">
                <span className="stat-label">Check-in</span>
                <span className="stat-value">{checkInPercentage}%</span>
              </div>
            )}
          </div>
          
          {/* Value Stats - Text only */}
          {artworks.length > 0 && (() => {
            const totalValue = artworks.reduce((sum, aw) => sum + (Number(aw.price) || 0), 0);
            const acceptedValue = artworks.filter(aw => aw.accepted).reduce((sum, aw) => sum + (Number(aw.price) || 0), 0);
            const acceptedCount = artworks.filter(aw => aw.accepted).length;
            const nfsCount = artworks.filter(aw => !aw.price || aw.price === 0 || aw.price === '0').length;
            return (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '1.5rem', 
                marginTop: '0.75rem',
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.7)'
              }}>
                <span>Total Value: <strong style={{ color: '#4ade80' }}>${totalValue.toLocaleString()}</strong></span>
                <span>Accepted: <strong style={{ color: 'white' }}>{acceptedCount}</strong> · <strong style={{ color: '#4ade80' }}>${acceptedValue.toLocaleString()}</strong></span>
                <span>Not For Sale: <strong style={{ color: '#fbbf24' }}>{nfsCount}</strong></span>
              </div>
            );
          })()}
        </div>

        {/* Right: Curator Tools */}
        <div className="event-banner-actions">
          {curatorToolsProps && (
            <CuratorToolsDropdown {...curatorToolsProps} />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventHeaderBanner;
